import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, message } from 'antd';
import { c205Api, FC130 } from '../../services/c205Api';
import { useC205FormState } from './hooks/useFormState';
import { useC205FormActions } from './hooks/useFormActions';
import { useModulePermission } from '../../hooks/usePermission';
import PermissionGuard from '../../components/PermissionGuard';
import MasterFields from './components/MasterFields';
import DetailTable from './components/DetailTable';
import BoardDetailTable from './components/BoardDetailTable';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import { BatchAcceptanceBridge, pwc205f1AcceptanceManifest } from '../../features/acceptance';
import './C205Form.css';

const PG_CODE = 'pwc205f1';

const C205Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [activeTab, setActiveTab] = useState(0);
  const [sourceType, setSourceType] = useState<'delivery' | 'sample'>('delivery');
  const [sourceNo, setSourceNo] = useState('');
  const permissions = useModulePermission(PG_CODE);

  // -- State hook --
  const {
    mode, setMode, isEditing,
    record, formData, details, boardDetails, selectedRow, loading,
    setSelectedRow, setDetails, setBoardDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError,
    lastApiResponse, setLastApiResponse,
  } = useC205FormState();

  // -- Navigation state --
  const [allIds, setAllIds] = useState<string[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);

  const loadList = useCallback(async (): Promise<string[]> => {
    const res = await c205Api.list(1, 10000);
    setLastApiResponse(res);
    const ids = (res.data || []).map(r => r.fc130_01).filter(Boolean);
    setAllIds(ids);
    return ids;
  }, [setLastApiResponse]);

  const loadRecordWithNames = useCallback(async (id: string) => {
    await loadRecord(id);
  }, [loadRecord]);

  const navTo = useCallback(async (idx: number, editing: boolean) => {
    if (idx < 0 || idx >= allIds.length || editing) return;
    setCurrentIdx(idx);
    await loadRecordWithNames(allIds[idx]);
  }, [allIds, loadRecordWithNames]);

  // -- Actions hook --
  const {
    handleAddAction, handleModifyAction,
    handleDeleteAction, handleNavTo,
  } = useC205FormActions({
    mode, setMode, isEditing, record, currentIdx,
    handleSave, handleDelete,
    loadList, setCurrentIdx, loadRecordWithNames,
    applyResult, resetForAdd, revertToRecord, navTo,
  });

  // -- Initial Load --
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewId) {
          const idx = ids.indexOf(reviewId);
          if (idx >= 0) setCurrentIdx(idx);
          await loadRecordWithNames(reviewId);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load C205 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames]);

  // -- Approval Handlers --
  const handleSubmit = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將退回單 ${record.fc130_01} 送審？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await c205Api.submitForApproval(record.fc130_01);
          setLastApiResponse(resp);
          message.success(resp.message || '送審成功');
          await loadRecordWithNames(record.fc130_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames, setLastApiResponse]);

  const handleApprove = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准退回單 ${record.fc130_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await c205Api.approve(record.fc130_01);
          setLastApiResponse(resp);
          message.success(resp.message || '核准成功');
          await loadRecordWithNames(record.fc130_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames, setLastApiResponse]);

  const handleReject = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回退回單 ${record.fc130_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await c205Api.reject(record.fc130_01);
          setLastApiResponse(resp);
          message.success(resp.message || '退回成功');
          await loadRecordWithNames(record.fc130_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames, setLastApiResponse]);

  const handleWriteoff = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fc130_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await c205Api.writeoff(record.fc130_01);
          setLastApiResponse(resp);
          message.success(resp.message || '沖銷成功');
          await loadRecordWithNames(record.fc130_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames, setLastApiResponse]);

  const [showHistory, setShowHistory] = useState(false);
  const [showBoard, setShowBoard] = useState(false);

  // -- Computed Values --
  const totalReturnQty = formData.fc130_13 || 0;
  const netAmount = formData.fc130_15 || 0;
  const taxAmount = formData.fc130_16 || 0;
  const totalWithTax = netAmount + taxAmount;

  // -- Render --
  return (
    <PermissionGuard module={PG_CODE} action="query">
    <div className="c205-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">

        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>訂單管理</a>
              <span>/</span>
              <span className="current">銷貨退回單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>銷貨退回單 Sales Return</h1>
            <span className="badge badge-default">C205</span>
          </div>
        </div>

        {/* Form-level fields — matches FoxPro layout (fc130_01 + fc130_11 outside tabs) */}
        <div className="search-bar">
          <div className="field-group">
            <label>銷貨退回單號</label>
            <input type="text" readOnly style={{ width: 120 }}
              value={record?.fc130_01 || ''} data-contract-field="fc130_01" />
          </div>
          {(() => {
            const s80 = (formData.fc130_80 || '').trim();
            return s80 === 'P' ? (
              <label data-contract-field="fc130_80" style={{ color: 'red', fontWeight: 'bold', fontSize: 14, border: '1px solid red', padding: '2px 8px' }}>已核准</label>
            ) : ['D', 'S'].includes(s80) ? ( // 統一碼 D + 舊碼 S 過渡容忍
              <label data-contract-field="fc130_80" style={{ color: '#faad14', fontWeight: 'bold', fontSize: 14, border: '1px solid #faad14', padding: '2px 8px' }}>待核准</label>
            ) : (
              <label data-contract-field="fc130_80" style={{ color: '#555', fontWeight: 'bold', fontSize: 14, border: '1px solid #bbb', padding: '2px 8px' }}>草稿</label>
            );
          })()}
          <div className="field-group" style={{ marginLeft: 'auto' }}>
            <label>退回日期</label>
            <input type="date" style={{ width: 150 }}
              disabled={!isEditing}
              value={formData.fc130_11 ? String(formData.fc130_11).substring(0, 10) : ''}
              onChange={e => {
                const v = e.target.value;
                setField('fc130_11', v ? v + 'T00:00:00Z' : null);
              }} data-contract-field="fc130_11"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['銷貨退回', '產品顏色批號明細'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: 銷貨退回單頭 */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <MasterFields
              formData={formData}
              isEditing={isEditing}
              getField={getField}
              setField={setField}
              getFieldError={getFieldError}
              totalReturnQty={totalReturnQty}
              netAmount={netAmount}
              taxAmount={taxAmount}
              totalWithTax={totalWithTax}
            />
          </div>

          {/* Tab 2: 產品顏色批號明細 */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            {mode === 'add' && (
              <div className="detail-summary" style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                <label>來源單</label>
                <select value={sourceType} onChange={e => setSourceType(e.target.value as 'delivery' | 'sample')}>
                  <option value="delivery">交運單</option><option value="sample">樣品退回單</option>
                </select>
                <input value={sourceNo} onChange={e => setSourceNo(e.target.value)} placeholder="來源單號" />
                <button className="btn btn-sm" onClick={async () => {
                  if (!sourceNo.trim()) { message.warning('請輸入來源單號'); return; }
                  try {
                    const preview = await c205Api.importSource(sourceType, sourceNo.trim());
                    setField('fc130_10', preview.fc130_10); setField('fc130_18', preview.fc130_18);
                    setField('fc130_20', preview.fc130_20); setField('fc130_19', preview.fc130_19);
                    setDetails(preview.details || []); message.success(`已帶入 ${preview.details?.length || 0} 筆（尚未儲存）`);
                  } catch (err: any) { message.error(err?.response?.data?.error || '來源帶入失敗'); }
                }}>帶入明細</button>
                <span style={{ fontSize: 12, color: '#666' }}>預覽不寫入，儲存後才建立退回單。</span>
              </div>
            )}
            <div className="detail-summary" style={{ marginBottom: 8, display: 'flex', gap: 16, alignItems: 'center' }}>
              <label data-contract-field="fc130_19">總項數</label>
              <input type="text" readOnly style={{ width: 60, textAlign: 'right' }}
                value={String(formData.fc130_19 || 0)} />
              <button
                className="btn btn-sm"
                onClick={() => setShowBoard(prev => !prev)}
              >
                {showBoard ? '隱藏板號' : '板號'}
              </button>
            </div>
            {showBoard && (
              <BoardDetailTable
                boardDetails={boardDetails}
                isEditing={isEditing}
                setBoardDetails={setBoardDetails}
              />
            )}
            <DetailTable
              details={details}
              selectedRow={selectedRow}
              isEditing={isEditing}
              setSelectedRow={setSelectedRow}
              setDetails={setDetails}
              addDetailRow={addDetailRow}
              deleteDetailRow={deleteDetailRow}
            />
          </div>

        </div>{/* /tabs */}

        {/* Bottom Action Bar (SharedActionBar) */}
        {(() => {
          const s80 = (formData.fc130_80 || '').trim();
          const approval: ApprovalState = {
            statusCode: s80,
            isApproved: s80 === 'P',
            isPending: ['D', 'S'].includes(s80), // 統一碼 D + 舊碼 S 過渡容忍
            isLocked: ['P', 'D', 'S'].includes(s80),
          };
          return (
            <SharedActionBar
              mode={mode} isEditing={isEditing} hasRecord={!!record}
              currentIdx={currentIdx} recordCount={allIds.length}
              onNavFirst={() => handleNavTo(0)} onNavPrev={() => handleNavTo(currentIdx - 1)}
              onNavNext={() => handleNavTo(currentIdx + 1)} onNavLast={() => handleNavTo(allIds.length - 1)}
              onAdd={handleAddAction} onModify={handleModifyAction} onDelete={handleDeleteAction}
              onSave={handleAddAction} onCancel={handleModifyAction}
              approval={approval}
              onSubmit={handleSubmit} onApprove={handleApprove}
              onReject={handleReject} onWriteoff={handleWriteoff} onHistory={() => setShowHistory(true)}
              onExit={() => navigate('/erp')}
              canCreate={permissions.canCreate} canEdit={permissions.canEdit}
              canDelete={permissions.canDelete} canApprove={permissions.canApprove}
              canPrint={permissions.canPrint}
              acceptanceActionIds={{
                create: 'PWC205-ACT-CREATE',
                update: 'PWC205-ACT-UPDATE',
                delete: 'PWC205-ACT-DELETE',
                submit: 'PWC205-ACT-SUBMIT',
                approve: 'PWC205-ACT-APPROVE',
                reject: 'PWC205-ACT-REJECT',
                writeoff: 'PWC205-ACT-WRITEOFF',
              }}
            />
          );
        })()}
        <BatchAcceptanceBridge
          manifest={pwc205f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record ? [record.fc130_01] : []}
          keyOf={(item) => item.fc130_01}
          queryExact={(key) => c205Api.get(key)}
          lastApiResponse={lastApiResponse}
          loading={loading}
          onAutoFill={() => {
            if (allIds.length > 0) void navTo(0, false);
          }}
          autoFillDisabled={allIds.length === 0}
          autoFillReason="自動填表只載入第一筆既有銷退單，不會自動送審、核准或沖銷。"
          screenDraft={{ mode, master: formData, details, board_details: boardDetails }}
        />

      </div>{/* /page-wrapper */}
    </div>
    </PermissionGuard>
  );
};

export default C205Form;
